import { useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { CircleCheck } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import Checkbox from '@/components/form/Checkbox';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

import SignaturePad from '@/app/signature/SignaturePad';

import ContractorAgreement from './ContractorAgreement';

import { getPresignedUrl } from '@/data/file';
import {
  useCoachActivity,
  useCoachActivityAcceptTerms
} from '@/data/coachActivity';
import { useApplication } from '@/data/application';

const formSchema = z.object({
  isAgreedToTerms: z
    .boolean()
    .refine((val) => val === true, 'You must accept the terms and conditions'),
  signatureUrl: z.string().url('Required')
});

const APPLICATION_STEP_KEY = 'contractor-agreement';

const ContractorAgreementForm = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const coachActivityAcceptTerms = useCoachActivityAcceptTerms();
  const applicationStructure = useApplication({
    coachActivityId: params.coachActivityId
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isAgreedToTerms: false,
      signatureUrl: ''
    }
  });

  const step = applicationStructure.data.steps.find(
    (step) => step.key === APPLICATION_STEP_KEY
  );

  const handleSaveSignature = async (signature) => {
    try {
      setApiError('');
      // Convert data URL to Blob
      const response = await fetch(signature);
      const blob = await response.blob();

      // Get a presigned url for the signature
      const presignedUrl = await getPresignedUrl(
        coachActivity.data.userId,
        `signature-${coachActivity.data.id}`,
        'image/png',
        'png'
      );

      // Upload the signature to the presigned url
      await axios.put(presignedUrl, blob, {
        headers: {
          'Content-Type': 'image/png'
        }
      });

      const urlObject = new URL(presignedUrl);
      const documentPath = `${urlObject.origin}${urlObject.pathname}`;
      form.setValue('signatureUrl', documentPath);
    } catch (err) {
      setApiError(err.message);
    }
  };

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const updatedCoachActivity = await coachActivityAcceptTerms(
        params.coachActivityId,
        values
      );
      coachActivity.mutate(updatedCoachActivity, false);
      navigate(step.nextStepLink);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    formState: { errors }
  } = form;

  if (step.isComplete) {
    return (
      <>
        <Alert variant="success">
          <CircleCheck size={18} />
          <AlertTitle>Completed</AlertTitle>
          <AlertDescription>
            You have already signed the contractor agreement for{' '}
            {coachActivity.data.activity.title}.
          </AlertDescription>
          <div className="flex justify-end mt-4">
            <Link to={step.nextStepLink}>
              <Button size="sm">Continue</Button>
            </Link>
          </div>
        </Alert>
        <ContractorAgreement />
      </>
    );
  }

  return (
    <>
      <ContractorAgreement />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Checkbox
              name="isAgreedToTerms"
              label="I accept the terms and conditions"
            />
          </div>
          <h3>Signature</h3>
          <SignaturePad onSave={handleSaveSignature} />
          {errors.signatureUrl && (
            <span className="text-destructive">
              {errors.signatureUrl.message}
            </span>
          )}
          {apiError && <Error error={apiError} />}
          <div className="flex justify-end mt-8">
            <SpinnerButton loading={submitting} type="submit">
              Continue
            </SpinnerButton>
          </div>
        </form>
      </Form>
    </>
  );
};

export default ContractorAgreementForm;
